import { Extension } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import type { Node } from '@tiptap/pm/model';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { parseIngredient } from '../utils/ingredient-parser';

/**
 * Creates highlighting decorations using the new parseIngredient function
 */
function createIngredientDecorations(doc: Node): DecorationSet {
  const decorations: Decoration[] = [];

  doc.descendants((node, position) => {
    if (!node.text) {
      return;
    }

    const nodeText = node.text;

    // Split by lines to parse each line individually (ingredient lines)
    const lines = nodeText.split('\n');
    let currentLinePos = 0;

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (trimmedLine) {
        const parsed = parseIngredient(trimmedLine);

        // Find the actual position of the trimmed content in the original line
        const lineStartInText = nodeText.indexOf(trimmedLine, currentLinePos);
        if (lineStartInText !== -1) {
          const absoluteLineStart = position + lineStartInText;

          // Highlight quantity (purple underline)
          if (parsed.quantity) {
            const quantityDecoration = Decoration.inline(
              absoluteLineStart + parsed.quantity.startPos,
              absoluteLineStart + parsed.quantity.endPos,
              {
                class:
                  'quantity-highlight underline decoration-purple-600 decoration-2 underline-offset-1',
              },
            );
            decorations.push(quantityDecoration);
          }

          // Highlight unit (blue underline)
          if (parsed.unit) {
            const unitDecoration = Decoration.inline(
              absoluteLineStart + parsed.unit.startPos,
              absoluteLineStart + parsed.unit.endPos,
              {
                class:
                  'unit-highlight underline decoration-blue-500 decoration-2 underline-offset-1',
              },
            );
            decorations.push(unitDecoration);
          }

          // Highlight ingredient name (yellow underline)
          if (parsed.name) {
            const nameDecoration = Decoration.inline(
              absoluteLineStart + parsed.name.startPos,
              absoluteLineStart + parsed.name.endPos,
              {
                class:
                  'ingredient-highlight underline decoration-yellow-500 decoration-2 underline-offset-1',
              },
            );
            decorations.push(nameDecoration);
          }
        }
      }

      currentLinePos += line.length + 1; // +1 for the newline character
    }
  });

  return DecorationSet.create(doc, decorations);
}

export const IngredientHighlighter = Extension.create({
  name: 'ingredientHighlighter',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        state: {
          init(_, { doc }) {
            return createIngredientDecorations(doc);
          },
          apply(transaction, oldState) {
            return transaction.docChanged
              ? createIngredientDecorations(transaction.doc)
              : oldState;
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },
});
